import { formatAuditAction as formatAction, mergeAuditNotifications } from '@/lib/auditDisplay';
/**
 * SuperadminNotificationContext
 *
 * Provides real-time audit log notifications for all authenticated users.
 * Polls audit endpoints every 10 seconds and shows toast notifications
 * for new activity (logins, CRUD operations, etc.)
 *
 * Role-based access:
 * - Superadmins: See ALL audit activity globally
 * - Admins/Company Admins: See all user activity for their company scope
 * - Group Admins: See all user activity for their group scope
 * - Regular Users: See only their own activity
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  LogIn,
  LogOut,
  UserPlus,
  UserPen,
  UserX,
  KeyRound,
  FolderPlus,
  FolderPen,
  FolderX,
  Building2,
  Building,
  Trash2,
  Truck,
  Pencil,
  Package,
  Bell,
  MapPin,
  LucideIcon
} from 'lucide-react';
import { useRBAC } from './RBACContext';
import { getGlobalAuditLogs, getAuditLogs } from '../lib/adminApi';
import type { AuditLog } from '../types';
import {
  SuperadminNotificationContext,
  type NotificationContextValue,
} from './SuperadminNotificationContext';

// Polling interval in milliseconds (10 seconds)
const POLL_INTERVAL = 10000;

// Action icons for notifications (Lucide icons)
const ACTION_ICONS: Record<string, LucideIcon> = {
  'User Login': LogIn,
  'Failed Login': LogIn,
  'User Logout': LogOut,
  'Create User': UserPlus,
  'Update User': UserPen,
  'Delete User': UserX,
  'Password Reset': KeyRound,
  'Password Change': KeyRound,
  'Create Group': FolderPlus,
  'Update Group': FolderPen,
  'Delete Group': FolderX,
  'Create Company': Building2,
  'Update Company': Building,
  'Delete Company': Trash2,
  'Create Unit': Truck,
  'Update Unit': Pencil,
  'Delete Unit': Trash2,
  'Bulk Operation': Package,
};

export function SuperadminNotificationProvider({ children }: { children: React.ReactNode }) {
  const { isSuperadmin, isAdmin, isCompanyAdmin, isGroupAdmin, loading: rbacLoading, user } = useRBAC();
  
  // Determine if user is authenticated and their role level for polling
  const isAuthenticated = !!user;
  // Admins and above see all activity in their scope; regular users see only their own
  const isAdminLevel = isSuperadmin || isAdmin || isCompanyAdmin || isGroupAdmin;

  // localStorage key for persisting notifications
  const STORAGE_KEY = 'ht_activity_notifications';

  // State - load from localStorage on mount
  const [recentNotifications, setRecentNotifications] = useState<AuditLog[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPollTime, setLastPollTime] = useState<Date | null>(null);

  // Track the last timestamp we've seen to only fetch new logs
  const lastTimestampRef = useRef<string | null>(null);
  // Track if tab is visible
  const isVisibleRef = useRef(true);
  // Track if this is the first poll (to avoid showing all historical logs)
  const isFirstPollRef = useRef(true);
  // Track if initial poll has been triggered (prevents StrictMode double-mount)
  const hasInitialPollRef = useRef(false);

  // Get icon component for action (supports both formatted and SNAKE_CASE actions)
  const getActionIcon = (action: string): LucideIcon => {
    const formatted = formatAction(action);
    return ACTION_ICONS[formatted] || ACTION_ICONS[action] || Bell;
  };

  // Get severity color classes (matches audit page styling)
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return 'text-error bg-zinc-800/60';
      case 'warning': return 'text-warning bg-zinc-800/60';
      default: return 'text-muted-foreground bg-zinc-800/60';
    }
  };

  // Format role for display (e.g., "group_admin" → "Group Admin")
  const formatRole = (role: string): string => {
    if (!role || role === '-') return '';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  // Navigate to audit page based on user role
  const navigateToAudit = useCallback(() => {
    if (isSuperadmin) {
      window.location.href = '/settings/global-audit';
    } else {
      window.location.href = '/settings/audit';
    }
  }, [isSuperadmin]);

  // Custom toast content matching audit page style - clickable to navigate to audit
  const ToastContent = ({ log }: { log: AuditLog }) => {
    const Icon = getActionIcon(log.action);
    const displayName = log.name || log.user || 'System'; // Name, fallback to email, then System
    const formattedRole = formatRole(log.role);
    const hasContext = log.company || formattedRole;

    // Format location display
    const locationText = log.location && log.location !== 'Local'
      ? log.location
      : log.ip === '::1' || log.ip === '127.0.0.1'
        ? 'Local'
        : log.ip || 'Unknown';

    return (
      <div
        className="flex items-start gap-2.5 cursor-pointer"
        onClick={navigateToAudit}
        title="Click to view audit log"
      >
        {/* Severity-colored icon container */}
        <div className={`p-1.5 rounded-md flex-shrink-0 ${getSeverityColor(log.severity)}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          {/* Top row: Action + Location */}
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium text-sm text-white truncate">{formatAction(log.action)}</div>
            <div className="flex items-center gap-1 text-[10px] text-zinc-500 flex-shrink-0">
              <MapPin className="w-2.5 h-2.5" />
              <span>{locationText}</span>
            </div>
          </div>
          {/* Name */}
          <div className="text-xs text-zinc-300 truncate">{displayName}</div>
          {/* Company & Role */}
          {hasContext && (
            <div className="text-xs text-zinc-400 truncate">
              {formattedRole}{formattedRole && log.company ? ' · ' : ''}{log.company || ''}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show toast notification for new audit log
  const showNotificationToast = useCallback((log: AuditLog) => {
    // Use toastId to prevent duplicate toasts (e.g., from StrictMode double-renders)
    const toastId = `audit-${log.id}`;

    const toastOptions = {
      position: 'bottom-right' as const,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      toastId, // Prevents duplicate toasts for the same log
    };

    // Use different toast types based on severity
    if (log.severity === 'critical') {
      toast.error(<ToastContent log={log} />, {
        ...toastOptions,
        autoClose: 8000,
      });
    } else if (log.severity === 'warning') {
      toast.warning(<ToastContent log={log} />, {
        ...toastOptions,
        autoClose: 5000,
      });
    } else {
      toast.info(<ToastContent log={log} />, {
        ...toastOptions,
        autoClose: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToAudit]);

  // Fetch audit logs based on user's role level
  const fetchAuditLogs = useCallback(async (params: { since?: string; limit?: number }) => {
    if (isSuperadmin) {
      // Superadmins see all global activity
      return getGlobalAuditLogs(params);
    } else if (isAdminLevel) {
      // Admins see all activity in their company/group scope
      return getAuditLogs(params);
    } else {
      // Regular users see only their own activity
      return getAuditLogs({ ...params, user_only: true });
    }
  }, [isSuperadmin, isAdminLevel]);

  // Poll for new audit logs
  const pollForNewLogs = useCallback(async () => {
    // Don't poll if not authenticated, disabled, or tab not visible
    if (!isAuthenticated || !isEnabled || !isVisibleRef.current) {
      return;
    }

    setIsPolling(true);

    try {
      // On first poll, fetch the last 50 logs to populate the notification center
      // (don't show toast notifications for these historical logs)
      if (isFirstPollRef.current) {
        const { logs } = await fetchAuditLogs({ limit: 50 });
        if (logs.length > 0) {
          lastTimestampRef.current = logs[0].timestamp;
          // Populate notification center with historical logs (no toasts)
          setRecentNotifications(prev => mergeAuditNotifications(prev, logs));
        } else {
          // No logs yet, use current time
          lastTimestampRef.current = new Date().toISOString();
        }
        isFirstPollRef.current = false;
        setLastPollTime(new Date());
        return;
      }

      // Fetch logs since last timestamp
      const params = lastTimestampRef.current
        ? { since: lastTimestampRef.current }
        : { limit: 10 };

      const { logs: newLogs } = await fetchAuditLogs(params);

      if (newLogs.length > 0) {
        // Update last timestamp to the most recent log
        lastTimestampRef.current = newLogs[0].timestamp;

        // Add new logs to recent notifications (keep last 50)
        setRecentNotifications(prev => mergeAuditNotifications(prev, newLogs));

        // Update unread count
        setUnreadCount(prev => prev + newLogs.length);

        // Show toast notifications (exclude own actions — already shown locally)
        const otherUsersLogs = newLogs.filter(log => log.user !== user?.email);
        if (otherUsersLogs.length > 3) {
          // Use the first filtered log's timestamp as unique ID for grouped toast
          const groupToastId = `audit-group-${otherUsersLogs[0].timestamp}`;
          toast.info(
            <div
              className="flex items-start gap-2.5 cursor-pointer"
              onClick={navigateToAudit}
              title="Click to view audit log"
            >
              <div className="p-1.5 rounded-md flex-shrink-0 text-muted-foreground bg-zinc-800/60">
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm text-white">{otherUsersLogs.length} new activities</div>
                <div className="text-xs text-zinc-400">Click to view audit log</div>
              </div>
            </div>,
            {
              position: 'bottom-right',
              autoClose: 4000,
              toastId: groupToastId,
            }
          );
        } else if (otherUsersLogs.length > 0) {
          // Show individual toasts for each new log (excluding own)
          otherUsersLogs.forEach(log => {
            showNotificationToast(log);
          });
        }
      }

      setLastPollTime(new Date());
    } catch (error) {
      console.error('[Notifications] Failed to poll audit logs:', error);
    } finally {
      setIsPolling(false);
    }
  }, [isAuthenticated, isEnabled, showNotificationToast, fetchAuditLogs, navigateToAudit]);

  // Set up polling interval
  useEffect(() => {
    if (!isAuthenticated || rbacLoading || !isEnabled) {
      return;
    }

    // Prevent double initial poll from StrictMode double-mount
    if (!hasInitialPollRef.current) {
      hasInitialPollRef.current = true;
      pollForNewLogs();
    }

    // Set up interval
    const intervalId = setInterval(pollForNewLogs, POLL_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, rbacLoading, isEnabled, pollForNewLogs]);

  // Handle page visibility changes (pause polling when tab is hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      // Poll immediately when tab becomes visible
      if (isVisibleRef.current && isAuthenticated && isEnabled) {
        pollForNewLogs();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isEnabled, pollForNewLogs]);

  // Persist notifications to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentNotifications));
    } catch (e) {
      console.warn('[Notifications] Failed to persist to localStorage:', e);
    }
  }, [recentNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Clear all notifications (marks as read only - notifications are never truly cleared)
  // The notification center should always show the last 10+ logs
  const clearNotifications = useCallback(() => {
    // Only reset unread count, don't clear the notification list
    setUnreadCount(0);
  }, []);

  // Context value
  const value: NotificationContextValue = {
    recentNotifications,
    unreadCount,
    markAllAsRead,
    clearNotifications,
    isEnabled,
    setIsEnabled,
    isPolling,
    lastPollTime,
  };

  return (
    <SuperadminNotificationContext.Provider value={value}>
      {children}
    </SuperadminNotificationContext.Provider>
  );
}
